export const TIMEZONE = 'Australia/Sydney'

/** The admin panel blue — every accent on this page comes from here. */
export const BLUE = {
  main: '#1A73E8',
  dark: '#0D47A1',
  soft: '#E8F0FE',
  line: '#C6DAFC',
  mutedBg: '#F1F3F4',
  mutedText: '#5F6368',
}

/** Turns any MUI Switch blue without touching the global theme. */
export const blueSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: BLUE.main },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: BLUE.main,
    opacity: 0.5,
  },
}

export const AUDIENCES = [
  { value: 'both', label: 'Both (Host + Creative)' },
  { value: 'host', label: 'Host only' },
  { value: 'creative', label: 'Creative only' },
]

export const TYPES = [
  { value: 'popup', label: 'Popup' },
  { value: 'banner', label: 'Banner' },
]

export const PLACEMENTS = {
  popup: [{ value: 'center', label: 'Center' }],
  banner: [
    { value: 'top', label: 'Top of page' },
    { value: 'bottom', label: 'Pinned to bottom' },
  ],
}

/**
 * Every status the API can report, styled in the panel blue.
 * Live is the only solid fill so it stands out down the column.
 */
export const STATUSES = [
  {
    value: 'live',
    label: 'Live',
    sx: { backgroundColor: BLUE.main, color: '#fff' },
  },
  {
    value: 'scheduled',
    label: 'Scheduled',
    sx: { backgroundColor: BLUE.soft, color: BLUE.main },
  },
  {
    value: 'paused',
    label: 'Paused',
    sx: { backgroundColor: '#fff', color: BLUE.main, border: `1px solid ${BLUE.line}` },
  },
  {
    value: 'expired',
    label: 'Expired',
    sx: { backgroundColor: BLUE.mutedBg, color: BLUE.mutedText },
  },
]

/** The only two worth filtering by — scheduled and expired are read off the table. */
export const FILTER_STATUSES = STATUSES.filter((item) =>
  ['live', 'paused'].includes(item.value)
)

/**
 * Offset of Australia/Sydney, in minutes, at a given instant.
 * Derived from Intl so DST (AEST vs AEDT) is handled automatically.
 */
const sydneyOffsetMinutes = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(date)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {})

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  )

  return (asUTC - date.getTime()) / 60000
}

/**
 * Admins schedule in Aloca's own timezone, not their browser's — a "10:00" start
 * has to mean 10:00 in Sydney whether it's typed from Sydney or Lahore.
 * Converts a datetime-local value read as Sydney wall time into a UTC ISO string.
 */
export const sydneyLocalToISO = (value) => {
  if (!value) return ''

  const [datePart, timePart = '00:00'] = value.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour, minute] = timePart.split(':').map(Number)

  const wallClock = Date.UTC(year, month - 1, day, hour, minute)

  // Two passes: the first offset guess can be wrong right at a DST boundary.
  let offset = sydneyOffsetMinutes(new Date(wallClock))
  offset = sydneyOffsetMinutes(new Date(wallClock - offset * 60000))

  return new Date(wallClock - offset * 60000).toISOString()
}

/** UTC ISO back into the "YYYY-MM-DDTHH:mm" a datetime-local input expects. */
export const isoToSydneyLocal = (iso) => {
  if (!iso) return ''

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const shifted = new Date(date.getTime() + sydneyOffsetMinutes(date) * 60000)

  return shifted.toISOString().slice(0, 16)
}

/** Short Sydney-time label for the table, e.g. "12 Aug 2026, 10:00 am". */
export const formatSydney = (iso) => {
  if (!iso) return '—'

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'

  return date.toLocaleString('en-AU', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const statusMeta = (status) =>
  STATUSES.find((item) => item.value === status) || {
    label: status || '—',
    sx: { backgroundColor: BLUE.mutedBg, color: BLUE.mutedText },
  }

/** Comma separated page globs <-> the array the API stores. */
export const pathsToText = (list) => (Array.isArray(list) ? list.join(', ') : '')
export const textToPaths = (text) =>
  String(text || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
